
import gql from 'graphql-tag';
import productPromotionFragment from '~/apollo/fragments/productPromotionFragment';
import protectionPlanScenariosQuery from '~/apollo/queries/product/protectionPlanScenario.gql';
import wlProtectionTooltips from '~/apollo/queries/product/tooltips/wlProtectionTooltips';
import commonProductDetailMixins from '~/mixins/commonProductDetailMixins.js';
import productPromotionMixins from '~/mixins/productPromotionMixins.js';
import productShortcutMixins from "~/mixins/productShortcutMixins.js";
import productInsuranceNameMixins from '~/mixins/productInsuranceNameMixins';
import commonProductYearMixins from '~/mixins/commonProductYearMixins.js'

export default {
  mixins: [commonProductDetailMixins, productPromotionMixins, productShortcutMixins, productInsuranceNameMixins, commonProductYearMixins],

  data() {
    return {
      savingPlanScenarios: null,
      loading: 0,
      currentLocale: this.$normalizeLocale(this.$i18n.locale),
    };
  },
  // page component definitions
  apollo: {
    $loadingKey: 'loading',
    savingPlanScenarios: {
      query: gql`
        ${protectionPlanScenariosQuery}
        ${productPromotionFragment}
      `,
      prefetch: true,
      variables() {
        return {
          scenario_code: this.$route.params.slug,
          locale: this.currentLocale,
        };
      },
    },
    wlProtectionTooltips: {
      query: wlProtectionTooltips,
      prefetch: true,
      variables() {
        return { currentlocale: this.$normalizeLocale(this.$i18n.locale) };
      },
      update(data) {
        return this.$normalizeStrapi(data.infoTooltip.data);
      },
    },
  },
  computed: {
    simpleItems() {
      if (this.savingPlanScenarios?.data[0]) {
        const item = this.savingPlanScenarios.data[0];
        const planName = item.attributes.saving_plan.data.attributes.saving_plan_translations.data[0].attributes.name;
        const insurer = item.attributes.saving_plan.data.attributes.product.data.attributes.insurance_company.data.attributes.insurance_company_translations.data[0].attributes.name;
        let promotions = null;
        let promotion = null;
        let promotion_last_call = null;

        let decoderCTALink = this.$generateWhatsappCTALink(this.$t('products.protection.pt_categoryName'), insurer, planName);
        let decoderCTAText = '';

        const getShortcut = this.getProductShortcut(item.attributes);
        const shortcut = getShortcut.protection;

        // check scenario level promo first , then plan level promotion
        if (item.attributes.product_promotions.data?.length > 0) {
          promotions = item.attributes.product_promotions.data;
        }
        if (item.attributes.saving_plan.data.attributes.product_promotions.data?.length > 0) {
          promotions = item.attributes.saving_plan.data.attributes.product_promotions.data;
        }

        if(promotions != null) {
          promotion = this.checkPromotionValid(promotions);
          promotion_last_call = this.checkPromotionLastCall(promotion);

          if (!promotion) {
            promotion = null;
          } else if (promotion.attributes?.product_promotion_translations?.data[0]?.attributes?.DecoderCTALink) {
            decoderCTALink =
              promotion.attributes?.product_promotion_translations?.data[0]?.attributes?.DecoderCTALink;
            decoderCTAText = this.$t('products.product_website');
          }

        }

        const filteredTranslations = this.getFilteredTranslations(item.attributes.saving_plan.data.attributes.product.data.attributes.insurance_company.data.attributes
        .insurance_company_translations.data[0].attributes.localizations.data);

        return {
          scenario_code: item.attributes.scenario_code,
          payment_terms: item.attributes.payment_terms,
          pre_pay: item.attributes.pre_pay,
          currency: item.attributes.currency.data.attributes.currency_translations.data[0].attributes,
          currency_code: item.attributes.currency.data.attributes.code,
          lifeprotection_score: item.attributes.lifeprotection_score,
          overall_star_protection: item.attributes.overall_star_protection,

          death_g_tot_avg_0_19: item.attributes.death_g_tot_avg_0_19,
          death_g_tot_avg_20_49: item.attributes.death_g_tot_avg_20_49,
          guarantee_breakeven: item.attributes.guarantee_breakeven,

          total_premium: item.attributes.total_premium,
          plan_name: item.attributes.saving_plan.data.attributes.saving_plan_translations.data[0].attributes.name,

          insurer_name_first:
            item.attributes.saving_plan.data.attributes.product.data.attributes.insurance_company.data.attributes
              .insurance_company_translations.data[0].attributes.name,
          insurer_name_second:
            filteredTranslations[0].attributes.name,
          insurer_logo:
            item.attributes.saving_plan.data.attributes.product.data.attributes.insurance_company.data.attributes.logo,
          insurer_code:
            item.attributes.saving_plan.data.attributes.product.data.attributes.insurance_company.data.attributes.code,

          financial_details: item.attributes.saving_plan_financial_details.data,
          product_promotion: promotion,
          promotion_last_call: promotion_last_call,

          decoder_cta_link: decoderCTALink,
          decoder_cta_text: decoderCTAText,

          // // ***** The 30th Years *****
          guarantee_cash: this.getFinancialDetails(item, 30).guarantee_cash,
          total_cash_pess: this.getFinancialDetails(item, 30).total_cash_pess,
          total_cash_base: this.getFinancialDetails(item, 30).total_cash_base,
          girr: this.getFinancialDetails(item, 30).girr,
          pirr_pess: this.getFinancialDetails(item, 30).pirr_pess,
          pirr_base: this.getFinancialDetails(item, 30).pirr_base,

          scenarioId: item.id,
          shortcut: shortcut,

          // WL Protection display update 2025
          earlier_years_booster: item.attributes.saving_plan.data.attributes.earlier_years_booster,
          accidental_death_benefit: item.attributes.saving_plan.data.attributes.accidental_death_benefit,
          terminal_illness_benefit: item.attributes.saving_plan.data.attributes.terminal_illness_benefit,
          premium_holiday: item.attributes.saving_plan.data.attributes.premium_holiday,
          guaranteed_insurability_opt: item.attributes.saving_plan.data.attributes.guaranteed_insurability_opt,
          premium_waiver: item.attributes.saving_plan.data.attributes.premium_waiver,
          death_settlement_opt: item.attributes.saving_plan.data.attributes.death_settlement_opt,
        };
      }
      return null;
    },
    product() {
      if (!this.simpleItems) return null;

      console.debug(this.simpleItems);

      return {
        highlightSection: {
          scenarioCode: this.simpleItems.scenario_code,
          scenarioId: this.simpleItems.scenarioId,
          showFiveStarBadge: this.$formatShowFiveStarBadge(this.simpleItems.overall_star_protection),
          productName: this.simpleItems.plan_name,
          insuranceCompany: {
            name: this.simpleItems.insurer_name_first + ' | ' + this.simpleItems.insurer_name_second,
            code: this.simpleItems.insurer_code,
            logoSrc: this.simpleItems.insurer_logo?.data?.attributes?.url,
            logoAlt: this.simpleItems.insurer_name_first,
          },
          userRating: {
            score: this.simpleItems.overall_star_protection,
            numberOfRatedUser: '0',
          },
          tenlifeScore: {
            score: parseFloat(this.simpleItems.lifeprotection_score),
            description: this.$t('products.protection.pt_wholeLifeProtectionScore'),
            toolTipHtml: this.getTooltipContentByKey('pt_wholeLifeProtectionScore'),
          },
          features: [
            {
              text: this.simpleItems.pre_pay
                ? this.$t('products.longSavings.ls_prepaid')
                : this.simpleItems.payment_terms + this.$t('products.protection.pt_yearpayment'),
              type: 'payment_term',
            },
            {
              text: this.getCurrency(this.simpleItems.currency),
              type: 'currency',
            },
          ],
          keyItems: [
            {
              value: this.$formatPrice(this.simpleItems.death_g_tot_avg_0_19),
              key: this.$t('products.protection.pt_age35_54AverageDeathBenefit'),
              topSubText: this.simpleItems.currency_code,
              toolTipHtml: this.getTooltipContentByKey('pt_age35_54AverageDeathBenefit'),
              symbolValue: '',
              originalValue: '',
            },
            {
              value: this.$formatPrice(this.simpleItems.death_g_tot_avg_20_49),
              key: this.$t('products.protection.pt_age55_84AverageDeathBenefit'),
              topSubText: this.simpleItems.currency_code,
              toolTipHtml: this.getTooltipContentByKey('pt_age55_84AverageDeathBenefit'),
              symbolValue: '',
              originalValue: '',
            },
            {
              value: this.$formatPrice(this.simpleItems.total_premium / this.simpleItems.payment_terms),
              key: this.$t('products.protection.pt_annualPremium'),
              topSubText: this.simpleItems.currency_code,
              toolTipHtml: '',
              symbolValue: '',
              originalValue: '',
            },
          ],
          mainButtonCTA: {
            text: this.simpleItems.decoder_cta_text,
            link: this.simpleItems.decoder_cta_link,
          },
          showShortcut: this.simpleItems.shortcut,
        },
        detailSection: {
          details: [
            {
              id: 0,
              sectionTitle: this.$t('products.protection.pt_overview'),
              keyValues: [
                {
                  key: this.$t('products.protection.pt_wholeLifeProtectionScore'),
                  value: this.$formatScore(this.simpleItems.lifeprotection_score) + ' / 10',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_currency'),
                  value: this.getCurrency(this.simpleItems.currency),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_paymentTermYearly'),
                  value: this.simpleItems.pre_pay
                    ? this.$t('products.longSavings.ls_prepaid')
                    : this.simpleItems.payment_terms + this.$t('products.protection.pt_yearpayment'),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_annualPremium'),
                  value:
                    this.simpleItems.currency_code +
                    ' ' +
                    this.$formatPrice(this.simpleItems.total_premium / this.simpleItems.payment_terms),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_totalPremiumPaid'),
                  value: this.simpleItems.currency_code + ' ' + this.$formatPrice(this.simpleItems.total_premium),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_guaranteedBreakevenYear'),
                  value: this.simpleItems.guarantee_breakeven===-1?this.$t('products.protection.pt_cannotBreakeven'):this.simpleItems.guarantee_breakeven,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
              ],
            },
            {
              id: 1,
              sectionTitle: this.$t('products.protection.pt_guaranteedDeathBenefit'),
              keyValues: [
                this.deathAgeGroupData(this.simpleItems, 1, 10, 35),
                this.deathAgeGroupData(this.simpleItems, 11, 20, 35),
                this.deathAgeGroupData(this.simpleItems, 21, 30, 35),
                this.deathAgeGroupData(this.simpleItems, 31, 40, 35),
                this.deathAgeGroupData(this.simpleItems, 41, 50, 35),
                this.deathAgeGroupData(this.simpleItems, 51, 60, 35),
                this.deathAgeGroupData(this.simpleItems, 61, 65, 35),
              ],
            },
            {
              id: 2,
              sectionTitle: this.$t('products.protection.pt_policyValue'),
              keyValues: [
                {
                  key: this.$t('products.protection.pt_30year'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_totalPremiumPaid'),
                  value: this.simpleItems.currency_code + ' ' + this.$formatPrice(this.simpleItems.total_premium),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.longSavings.ls_guaranteedCashValue'),
                  value: this.simpleItems.currency_code + ' ' + this.$formatPrice(this.simpleItems.guarantee_cash),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.longSavings.ls_projectedCashValuePessimistic'),
                  value: this.simpleItems.currency_code + ' ' + this.$formatPrice(this.simpleItems.total_cash_pess),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.longSavings.ls_projectedCashValueBase'),
                  value: this.simpleItems.currency_code + ' ' + this.$formatPrice(this.simpleItems.total_cash_base),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_IRR'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.longSavings.ls_guaranteedIRR'),
                  value: this.$formatPercentage(this.simpleItems.girr),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.longSavings.ls_projectedReturnPessimistic'),
                  value: this.$formatPercentage(this.simpleItems.pirr_pess),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.longSavings.ls_projectedReturnBase'),
                  value: this.$formatPercentage(this.simpleItems.pirr_base),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
              ],
            },
            {
              id: 3,
              sectionTitle: this.$t('products.protection.pt_specialFeature'),
              keyValues: [
                {
                  key: this.$t('products.protection.pt_earlyYearsBooster'),
                  value: this.$formatSymbol(this.simpleItems.earlier_years_booster),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_accidentalDeathBenefit'),
                  value: this.$formatSymbol(this.simpleItems.accidental_death_benefit),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_terminalIllnessBenefit'),
                  value: this.$formatSymbol(this.simpleItems.terminal_illness_benefit),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_premiumHoliday'),
                  value: this.$formatSymbol(this.simpleItems.premium_holiday),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_guaranteedInsurabilityOption'),
                  value: this.$formatSymbol(this.simpleItems.guaranteed_insurability_opt),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_premiumWaiver'),
                  value: this.$formatSymbol(this.simpleItems.premium_waiver),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.protection.pt_deathSettlementOptions'),
                  value: this.simpleItems.death_settlement_opt?this.simpleItems.death_settlement_opt+' '+this.$t('products.protection.pt_options'):'',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
              ],
            },
          ],
          promotion: {
            category:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.PromotionCategory,
            title:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.PromotionTitle,
            ctaButtonLink:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.CTAButtonLink,
            ctaButtonText:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.CTAButtonText,
            promotionId: this.simpleItems.product_promotion?.id,
            scenarioId: this.simpleItems.scenarioId,
            lastCall: this.simpleItems.promotion_last_call
          },
        },
      };
    },
  },

  methods: {
    getFinancialDetails(item, policyYear) {
      return item.attributes.saving_plan_financial_details.data.filter((details) => {
        return details.attributes.policy_year === parseInt(policyYear);
      })[0].attributes;
    },

    getCurrency(curr) {
      if (this.currentLocale === 'zh-Hant-HK') {
        return curr.text;
      } else {
        return curr.localizations.data[0].attributes.text;
      }
    },

    getAgeRange(from, to, lblAge, lblTo) {
      if (this.currentLocale === 'en') {
        return lblAge + ' ' + from + ' ' + lblTo + ' ' + to;
      } else if (this.currentLocale === 'zh-Hant-HK') {
        return from + lblTo + to + lblAge;
      }
    },

    getTooltipContentByKey(mKey) {
      return this.wlProtectionTooltips?.WLProtection?.ToolTips?.find(({ Key }) => Key === mKey)?.ToolTipContent ?? '';
    },

    deathAgeGroupData(item, fromPolicyYear, toPolicyYear, startAge) {
      const selectedAge = (fromPolicyYear, toPolicyYear) => {
        return item.financial_details.filter((agePremium) => {
          return (
            agePremium.attributes.policy_year >= fromPolicyYear && agePremium.attributes.policy_year <= toPolicyYear
          );
        });
      };

      const avgSelected = (fromPolicyYear, toPolicyYear) => {
        const totalPrice = 0;
        const currentSelectedAge = selectedAge(fromPolicyYear, toPolicyYear);
        const priceOnly = currentSelectedAge.map((x) => x.attributes.guarantee_death_benefit);
        const sumWithInitial = priceOnly.reduce((accumulator, currentValue) => accumulator + currentValue, totalPrice);
        return sumWithInitial / currentSelectedAge.length;
      };

      const getAge = (age, lblAge) => {
        return this.$i18n.locale === 'en' ? lblAge + ' ' + age : age + ' ' + lblAge;
      };

      const mCurrency = item.currency_code;
      const mAvgSelected = avgSelected(fromPolicyYear, toPolicyYear);
      const mSelectedAge = selectedAge(fromPolicyYear, toPolicyYear);

      return {
        key: fromPolicyYear===0?this.$t('products.protection.pt_first10Year'):this.$t('products.protection.pt_yearRange')
          .replace('$from', this.$t('products.protection.pt_ordinalNumber').replace('$num', this.calculateOridinalNumber(fromPolicyYear)))
          .replace('$to', this.$t('products.protection.pt_ordinalNumber').replace('$num', this.calculateOridinalNumber(toPolicyYear))),
        value: mCurrency + ' ' + this.$formatPrice(mAvgSelected),
        keyToolTipHtml: '',
        expandableKeyValues: mSelectedAge.map((x) => ({
          key: this.$t('products.protection.pt_year').replace('$year', this.$t('products.protection.pt_ordinalNumber').replace('$num', this.calculateOridinalNumber(x.attributes.policy_year))),
          value: mCurrency + ' ' + this.$formatPrice(x.attributes.guarantee_death_benefit),
        })),
      };
    },
  },
};
